/**
 * CSK Foundation Types — Categorical building blocks.
 *
 * Agda source: Foundation/Category/
 * These types encode the categorical structures that CSK theorems instantiate.
 * TypeScript's structural type system enforces shape constraints at compile time;
 * deeper categorical laws (functoriality, naturality) are documented but rely on
 * correct construction rather than type-level proof.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

// ── Core categorical types ─────────────────────────────────────

/** A branded type that prevents accidental substitution between domains. */
export type Brand<T, B extends string> = T & { readonly __brand: B };

/** Morphism from A to B in a category — a structure-preserving map. */
export interface Morphism<A, B> {
  readonly source: A;
  readonly target: B;
  readonly map: (a: A) => B;
}

/** A witness that a property holds — the constructive evidence CSK requires. */
export type Witness<T> = Brand<T, "Witness">;

/** Create a witness from evidence. */
export function witness<T>(evidence: T): Witness<T> {
  return evidence as Witness<T>;
}

// ── Safety-class markers ───────────────────────────────────────

/**
 * Compositional safety (innate immunity): compose freely, safety preserved.
 * The 5 compositional guarantees: DATA.ORIGIN, DATA.REACH, PERMIT, SEPARATE, PRECOMMIT.
 */
export type Compositional = Brand<"compositional", "SafetyClass">;

/**
 * Parametric safety (acquired immunity): re-verify after composition.
 * The 5 parametric guarantees: RES.CONSUME, RES.RELEASE, COMPOSE, INV.ESTABLISH, INV.PRESERVE.
 */
export type Parametric = Brand<"parametric", "SafetyClass">;

export type SafetyClass = Compositional | Parametric;

// ── Neutralization modes ───────────────────────────────────────

/** Runtime neutralization mode literals used by theorem metadata. */
export const NEUTRALIZATION_MODE_NAMES = ["immune", "contained", "inert"] as const;

/** Runtime neutralization mode literal. */
export type NeutralizationModeName = (typeof NEUTRALIZATION_MODE_NAMES)[number];

/** Human-facing display literals for neutralization modes. */
export const NEUTRALIZATION_MODE_DISPLAY_NAMES = ["IMMUNE", "CONTAINED", "INERT"] as const;

/** Uppercase display literal for UI surfaces and manuscript prose. */
export type NeutralizationModeDisplayName =
  (typeof NEUTRALIZATION_MODE_DISPLAY_NAMES)[number];

/**
 * IMMUNE: violations have no type representation — boundary property.
 * 5 guarantees: DATA.ORIGIN, DATA.REACH, PERMIT, SEPARATE, PRECOMMIT.
 */
export type Immune = Brand<"immune", "NeutralizationMode">;

/**
 * INERT: violations exist but cannot compose into exploits — algebraic property.
 * 3 guarantees: COMPOSE, INV.ESTABLISH, INV.PRESERVE.
 */
export type Inert = Brand<"inert", "NeutralizationMode">;

/**
 * CONTAINED: violations bounded by resource budget — quantitative property.
 * 2 guarantees: RES.CONSUME, RES.RELEASE.
 */
export type Contained = Brand<"contained", "NeutralizationMode">;

export type NeutralizationMode = Immune | Inert | Contained;

// ── Tier markers ───────────────────────────────────────────────

/** Tier 1: Type-theoretic — violations won't typecheck. */
export type Tier1 = Brand<1, "Tier">;

/** Tier 2: Structural-geometric — geometric constraints make violation impossible. */
export type Tier2 = Brand<2, "Tier">;

/** Tier 3: Quantitative — resource bounds prevent violation. */
export type Tier3 = Brand<3, "Tier">;

export type Tier = Tier1 | Tier2 | Tier3;

// ── Generator types ────────────────────────────────────────────

/** The four generators exhaust all ways security axioms can be violated. */
export const GENERATOR_CODES = ["G1", "G2", "G3", "G4"] as const;

/** Canonical generator code. */
export type GeneratorCode = (typeof GENERATOR_CODES)[number];

export interface Generator {
  readonly code: GeneratorCode;
  readonly name: string;
  readonly dimension: string;
  readonly blocking: readonly string[];
}

// ── Neutralization Theorem codes ──────────────────────────────

/** Canonical short codes for the ten Neutralization Theorems. */
export const NEUTRALIZATION_THEOREM_CODES = [
  "D1",
  "D2",
  "P",
  "S",
  "W",
  "R1",
  "R2",
  "C",
  "I1",
  "I2",
] as const;

/** Canonical Neutralization Theorem short code. */
export type NeutralizationTheoremCode =
  (typeof NEUTRALIZATION_THEOREM_CODES)[number];

// ── Compositionality Principle codes ──────────────────────────

/** Canonical short codes for the seven Structural Duality Principles. */
export const COMPOSITIONALITY_PRINCIPLE_CODES = [
  "Tr",
  "Al",
  "St",
  "At",
  "Dt",
  "De",
  "Pa",
] as const;

/** Canonical Structural Duality Principle short code. */
export type CompositionalityPrincipleCode =
  (typeof COMPOSITIONALITY_PRINCIPLE_CODES)[number];

// ── Seam types ─────────────────────────────────────────────────

/**
 * Seams are composition boundaries where safety must be re-verified.
 * The four seam orientations correspond to different composition patterns.
 */
export const SEAM_TYPES = ["vertical", "horizontal", "diagonal", "mixed"] as const;

/** Seam orientation. */
export type SeamType = (typeof SEAM_TYPES)[number];

// ── Vulnerability and violation ────────────────────────────────

/**
 * A vulnerability class — the abstract shape of what CSK neutralizes.
 * CSK makes these structurally inexpressible: no value of type Violation
 * can be constructed in a correctly-typed system.
 */
export interface VulnerabilityClass {
  readonly name: string;
  readonly generator: GeneratorCode;
  readonly guaranteeCode: string;
}

// ── Guarantee structure (the unified pattern) ──────────────────

/**
 * Every CSK guarantee (theorem) follows this unified structure.
 * This is the TypeScript realization of SecureGovernancePattern.agda.
 *
 * Agda source: Foundation/SecureGovernancePattern/
 * Realization fidelity: STRUCTURAL (shape enforced; categorical laws documented)
 */
export interface Guarantee<Structure, Violation> {
  /** The categorical structure that provides the safety property. */
  readonly structure: Structure;

  /**
   * The neutralization proof — demonstrates that the structure makes
   * violations unconstructable. In Agda this is a proof term; in TypeScript
   * it's a function that takes a violation witness and returns never.
   */
  readonly neutralize: (violation: Violation) => never;

  /** Metadata for the guarantee. */
  readonly meta: GuaranteeMeta;
}

export interface GuaranteeMeta {
  readonly code: string;
  readonly name: string;
  readonly legacy: string;
  readonly structure: string;
  readonly safetyClass: "compositional" | "parametric";
  readonly mode: NeutralizationModeName;
  readonly tier: 1 | 2 | 3;
  readonly seamType: SeamType;
  readonly generator: GeneratorCode;
  readonly agdaSource: string;
}
